<script setup>
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { createRealEstateBrokerDashboardPresenter } from '../composables/realEstateBrokerDashboardPresenter';

defineProps({
  loading: { type: Boolean, default: false },
  rows: { type: Array, default: () => [] },
});

const emit = defineEmits(['open']);

const { t } = useI18n();
const {
  money,
  statusLabel,
  statusClass,
  dateLabel,
  dateValue,
  address,
  addressDetail: detail,
  propertyCode,
  propertyImage,
  percentage,
  installmentLabel,
  transferProgressLabel,
} = createRealEstateBrokerDashboardPresenter(t);
</script>

<template>
  <div
    class="hidden overflow-x-auto rounded-lais border border-lais-border-soft bg-white shadow-lais-card lg:block"
  >
    <table class="w-full min-w-[1080px] text-left text-sm">
      <thead
        class="border-b border-lais-border-soft bg-lais-bg-soft text-xs uppercase tracking-wide text-lais-text-muted"
      >
        <tr>
          <th class="p-4">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.STATUS') }}
          </th>
          <th class="p-4">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.VALUE') }}
          </th>
          <th class="p-4">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.PROPERTY_CODE') }}
          </th>
          <th class="p-4">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.PROPERTY') }}
          </th>
          <th class="p-4">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.DATE') }}
          </th>
          <th class="p-4" />
        </tr>
      </thead>
      <tbody class="divide-y divide-lais-border-soft">
        <tr v-if="loading">
          <td colspan="6" class="p-8 text-center text-lais-text-muted">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.LOADING') }}
          </td>
        </tr>
        <tr v-else-if="!rows.length">
          <td colspan="6" class="p-8 text-center text-lais-text-muted">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.EMPTY') }}
          </td>
        </tr>
        <tr
          v-for="row in rows"
          :key="row.commission_split_id"
          class="cursor-pointer hover:bg-lais-bg-soft/60"
          tabindex="0"
          @click="emit('open', row.parcela_id)"
          @keydown.enter="emit('open', row.parcela_id)"
        >
          <td class="p-4">
            <div class="flex flex-wrap gap-2">
              <span
                class="rounded-full px-2 py-1 text-xs font-semibold"
                :class="statusClass(row)"
              >
                {{ statusLabel(row) }}
              </span>
              <span
                v-if="row.status?.grupo !== 'em_andamento'"
                class="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700"
              >
                {{ percentage(row) }}
              </span>
            </div>
          </td>
          <td class="p-4 font-semibold text-lais-text">
            <template v-if="row.progresso_transferencia">
              <p class="text-xs">{{ transferProgressLabel(row) }}</p>
              <progress
                class="mt-2 h-2 w-full accent-lais-primary"
                max="100"
                :value="row.progresso_transferencia.percentual"
              />
            </template>
            <template v-else>{{ money(row) }}</template>
            <p class="mt-1 text-xs font-normal text-lais-text-muted">
              {{ installmentLabel(row) }}
            </p>
          </td>
          <td class="p-4">
            <span
              class="rounded-full bg-lais-bg-soft px-2 py-1 text-xs font-semibold"
            >
              {{ propertyCode(row) }}
            </span>
          </td>
          <td class="p-4">
            <div class="flex min-w-64 items-center gap-3">
              <img
                v-if="propertyImage(row)"
                :src="propertyImage(row)"
                :alt="t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.PROPERTY_IMAGE')"
                class="size-10 rounded-full object-cover"
              />
              <span
                v-else
                class="i-lucide-image-off grid size-10 place-items-center rounded-full bg-lais-bg-soft text-lais-text-muted"
              />
              <div>
                <p class="font-semibold text-lais-text">{{ address(row) }}</p>
                <p v-if="detail(row)" class="text-xs text-lais-text-muted">
                  {{ detail(row) }}
                </p>
              </div>
            </div>
          </td>
          <td class="p-4 text-lais-text-body">
            <p class="text-xs font-semibold">{{ dateLabel(row) }}</p>
            <p>{{ dateValue(row) }}</p>
          </td>
          <td class="p-4">
            <LaisPrimaryButton
              size="sm"
              @click.stop="emit('open', row.parcela_id)"
            >
              {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.VIEW_VALUE') }}
            </LaisPrimaryButton>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
